import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/hooks/useAuth";
import { AppLayout } from "@/components/AppLayout";
import { ModuleGuard } from "@/components/ModuleGuard";
import Auth from "./pages/Auth";

// Retry wrapper for lazy imports — handles stale chunk errors after redeployment
function lazyRetry(factory: () => Promise<{ default: React.ComponentType<any> }>) {
  return lazy(() =>
    factory().catch((err) => {
      // If it's a chunk load failure, try a hard reload once
      const key = "chunk-reload-retry";
      const hasRetried = sessionStorage.getItem(key);
      if (!hasRetried) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise(() => {}); // never resolves — page is reloading
      }
      sessionStorage.removeItem(key);
      throw err;
    })
  );
}

// Lazy-loaded route components for code splitting
const Dashboard = lazyRetry(() => import("./pages/Dashboard"));
const KnowledgeHub = lazyRetry(() => import("./pages/knowledge/KnowledgeHub"));
const KnowledgePage = lazyRetry(() => import("./pages/knowledge/KnowledgePage"));
const Framework = lazyRetry(() => import("./pages/Framework"));
const FrameworkOverview = lazyRetry(() => import("./pages/FrameworkOverview"));
const Assessments = lazyRetry(() => import("./pages/Assessments"));
const AssessmentAnswer = lazyRetry(() => import("./pages/AssessmentAnswer"));
const CreateAssessment = lazyRetry(() => import("./pages/CreateAssessment"));
const Entities = lazyRetry(() => import("./pages/Entities"));
const Users = lazyRetry(() => import("./pages/Users"));
const Invitations = lazyRetry(() => import("./pages/Invitations"));
const Organisations = lazyRetry(() => import("./pages/Organisations"));
const ApprovalQueue = lazyRetry(() => import("./pages/ReviewQueue"));
const ApproveAssessment = lazyRetry(() => import("./pages/ReviewAssessment"));
const AssessmentReport = lazyRetry(() => import("./pages/AssessmentReport"));
const AssessmentSummary = lazyRetry(() => import("./pages/AssessmentSummary"));
const AuditTrail = lazyRetry(() => import("./pages/AuditTrail"));
const AiInsights = lazyRetry(() => import("./pages/AiInsights"));
const ExpertProfile = lazyRetry(() => import("./pages/ExpertProfile"));
const ExpertSessions = lazyRetry(() => import("./pages/ExpertSessions"));
const MyBookings = lazyRetry(() => import("./pages/MyBookings"));
const NotFound = lazyRetry(() => import("./pages/NotFound"));
const Privacy = lazyRetry(() => import("./pages/Privacy"));
const Terms = lazyRetry(() => import("./pages/Terms"));
const CookiePolicy = lazyRetry(() => import("./pages/CookiePolicy"));
const DataProcessingAgreement = lazyRetry(() => import("./pages/DataProcessingAgreement"));
const AcceptableUse = lazyRetry(() => import("./pages/AcceptableUse"));
const SrsDocument = lazyRetry(() => import("./pages/SrsDocument"));
const Marketplace = lazyRetry(() => import("./pages/Marketplace"));
const MarketplaceVendors = lazyRetry(() => import("./pages/MarketplaceVendors"));
const MarketplaceCategories = lazyRetry(() => import("./pages/MarketplaceCategories"));
const VendorProfile = lazyRetry(() => import("./pages/VendorProfile"));
const ExpertAdvisory = lazyRetry(() => import("./pages/ExpertAdvisory"));
const Services = lazyRetry(() => import("./pages/Services"));
const ServiceDetail = lazyRetry(() => import("./pages/ServiceDetail"));

const FinancialSupport = lazyRetry(() => import("./pages/FinancialSupport"));
const FundingDetail = lazyRetry(() => import("./pages/FundingDetail"));
const ExplorePlatform = lazyRetry(() => import("./pages/ExplorePlatform"));
const PressKit = lazyRetry(() => import("./pages/PressKit"));
const Methodology = lazyRetry(() => import("./pages/Methodology"));
const Partners = lazyRetry(() => import("./pages/Partners"));
const AdminServiceRequests = lazyRetry(() => import("./pages/AdminServiceRequests"));
const AdminMarketplaceLeads = lazyRetry(() => import("./pages/AdminMarketplaceLeads"));
const VendorOnboarding = lazyRetry(() => import("./pages/VendorOnboarding"));
const VendorDashboard = lazyRetry(() => import("./pages/VendorDashboard"));
const AdminVendors = lazyRetry(() => import("./pages/AdminVendors"));
const AgeFriendlyIndex = lazyRetry(() => import("./pages/AgeFriendlyIndex"));
const Settings = lazyRetry(() => import("./pages/Settings"));
const Welcome = lazyRetry(() => import("./pages/Welcome"));

const IntelligenceHome = lazyRetry(() => import("./pages/intelligence/IntelligenceHome"));
const KnowledgeRepository = lazyRetry(() => import("./pages/intelligence/KnowledgeRepository"));
// ResearchLibrary is now embedded as a tab inside KnowledgeRepository (Knowledge Library)
const DocumentIngestion = lazyRetry(() => import("./pages/intelligence/DocumentIngestion"));
const SemanticSearch = lazyRetry(() => import("./pages/intelligence/SemanticSearch"));
const KnowledgeGraph = lazyRetry(() => import("./pages/intelligence/KnowledgeGraph"));
const KnowledgeGraphExplorer = lazyRetry(() => import("./pages/intelligence/KnowledgeGraphExplorer"));
const RelationshipExplorer = lazyRetry(() => import("./pages/intelligence/RelationshipExplorer"));
const AssessmentIntelligence = lazyRetry(() => import("./pages/intelligence/AssessmentIntelligence"));
const MarketplaceIntelligence = lazyRetry(() => import("./pages/intelligence/MarketplaceIntelligence"));
const ExpertIntelligence = lazyRetry(() => import("./pages/intelligence/ExpertIntelligence"));
const DatasetBuilder = lazyRetry(() => import("./pages/intelligence/DatasetBuilder"));
const AgentManagement = lazyRetry(() => import("./pages/intelligence/AgentManagement"));
const OntologyManager = lazyRetry(() => import("./pages/intelligence/OntologyManager"));
const SlmReadinessCenter = lazyRetry(() => import("./pages/intelligence/SlmReadinessCenter"));
const ModelEvaluationLab = lazyRetry(() => import("./pages/intelligence/ModelEvaluationLab"));

// LIP (Longevity Intelligence Platform) pages
const LipPlatformHome = lazyRetry(() => import("./pages/lip/PlatformHome"));
const LipOntologyHome = lazyRetry(() => import("./pages/lip/OntologyHome"));
const LipCorpusHome = lazyRetry(() => import("./pages/lip/CorpusHome"));
const LipAiLayerHome = lazyRetry(() => import("./pages/lip/AiLayerHome"));
const LipApplicationsHome = lazyRetry(() => import("./pages/lip/ApplicationsHome"));
const LipRoadmap = lazyRetry(() => import("./pages/lip/Roadmap"));
const LipApiCatalog = lazyRetry(() => import("./pages/lip/ApiCatalog"));
const LipOntologyGovernance = lazyRetry(() => import("./pages/lip/OntologyGovernance"));
const LipCorpusQualityMonitor = lazyRetry(() => import("./pages/lip/CorpusQualityMonitor"));
const LipModelRegistry = lazyRetry(() => import("./pages/lip/ModelRegistry"));
const LipFineTuningReadiness = lazyRetry(() => import("./pages/lip/FineTuningReadiness"));
const LipKnowledgeHome = lazyRetry(() => import("./pages/lip/knowledge/KnowledgeHome"));
const LipKnowledgeSources = lazyRetry(() => import("./pages/lip/knowledge/SourceRegistry"));
const LipKnowledgePipeline = lazyRetry(() => import("./pages/lip/knowledge/ProcessingPipeline"));
const LipKnowledgeValidation = lazyRetry(() => import("./pages/lip/knowledge/ValidationQueue"));
const LipKnowledgeCoverage = lazyRetry(() => import("./pages/lip/knowledge/CoverageDashboard"));
const LipKnowledgeEvidence = lazyRetry(() => import("./pages/lip/knowledge/EvidenceFramework"));
const LipKnowledgeGovernance = lazyRetry(() => import("./pages/lip/knowledge/KnowledgeGovernance"));
const LipKnowledgeGaps = lazyRetry(() => import("./pages/lip/knowledge/GapIntelligence"));
const LipKnowledgeSlm = lazyRetry(() => import("./pages/lip/knowledge/SlmPreparationDashboard"));
const LipKnowledgeMetric = lazyRetry(() => import("./pages/lip/knowledge/MetricIntelligence"));
const LipKnowledgeExtractions = lazyRetry(() => import("./pages/lip/knowledge/Extractions"));
const LipKnowledgeRelationships = lazyRetry(() => import("./pages/lip/knowledge/Relationships"));
const LipKnowledgeHealth = lazyRetry(() => import("./pages/lip/knowledge/KnowledgeHealth"));
const LipKnowledgePipelineDetail = lazyRetry(() => import("./pages/lip/knowledge/PipelineDetail"));
const LipPublicPages = lazyRetry(() => import("./pages/lip/knowledge/PublicPagesEditor"));
const LipSearchMonitoring = lazyRetry(() => import("./pages/lip/knowledge/SearchMonitoring"));
const MetricsIndex = lazyRetry(() => import("./pages/knowledge/MetricPages").then((m) => ({ default: m.MetricsIndex })));
const MetricPage = lazyRetry(() => import("./pages/knowledge/MetricPages").then((m) => ({ default: m.MetricPage })));
const LipKnowledgeRetrieval = lazyRetry(() => import("./pages/lip/knowledge/RetrievalPlayground"));
const LipDatasetFactory = lazyRetry(() => import("./pages/lip/DatasetFactory"));
const LipDatasetValidation = lazyRetry(() => import("./pages/lip/DatasetValidationLab"));
const LipDatasetOrchestrator = lazyRetry(() => import("./pages/lip/DatasetOrchestrator"));
const LipModelRegistryLab = lazyRetry(() => import("./pages/lip/ModelRegistryLab"));
const LipModelComparisonLab = lazyRetry(() => import("./pages/lip/ModelComparisonLab"));
const LipBenchmarkCenter = lazyRetry(() => import("./pages/lip/BenchmarkCenter"));
const LipTrainingLab = lazyRetry(() => import("./pages/lip/training/TrainingLab"));
const LipTrainingRunDetail = lazyRetry(() => import("./pages/lip/training/TrainingRunDetail"));

// Testbed & Innovation Validation Hub
const TestbedHubHome = lazyRetry(() => import("./pages/testbed/TestbedHubHome"));
const TestbedProposalsList = lazyRetry(() => import("./pages/testbed/ProposalsList"));
const TestbedProposalWizard = lazyRetry(() => import("./pages/testbed/ProposalWizard"));
const TestbedProposalDetail = lazyRetry(() => import("./pages/testbed/ProposalDetail"));
const TestbedPartnerships = lazyRetry(() => import("./pages/testbed/PartnershipsDirectory"));
const TestbedEvidence = lazyRetry(() => import("./pages/testbed/EvidenceLibrary"));
const TestbedAgents = lazyRetry(() => import("./pages/testbed/AgentConsole"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

/**
 * Page fallback: stays blank for the first 150ms (most chunks load faster than that),
 * then shows a lightweight skeleton. Avoids the "loader flash" on snappy navigations.
 */
const PageFallback = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShow(true), 150);
    return () => clearTimeout(t);
  }, []);
  if (!show) return null;
  return (
    <div className="max-w-[1200px] mx-auto p-6 space-y-6">
      <div className="h-9 w-2/3 skeleton-base rounded-md" />
      <div className="h-4 w-1/2 skeleton-base rounded-md" />
      <div className="grid gap-4 md:grid-cols-3 mt-8">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-40 skeleton-base rounded-lg" />
        ))}
      </div>
    </div>
  );
};

/**
 * Prefetch the heaviest / most-visited route chunks during browser idle time
 * so subsequent navigations feel instant. Runs once after first paint.
 * Gated on auth state: anonymous visitors only get public chunks; signed-in
 * users additionally get the in-app chunks. Avoids wasting bandwidth on the
 * /auth landing for unauthenticated users.
 */
function prefetchRoutes(isAuthed: boolean) {
  const idle =
    (window as any).requestIdleCallback ||
    ((cb: () => void) => setTimeout(cb, 1));
  idle(() => {
    // Public-facing chunks — useful for everyone
    import("./pages/Services");
    import("./pages/Marketplace");
    import("./pages/ExpertAdvisory");
    import("./pages/FinancialSupport");
    if (isAuthed) {
      import("./pages/Dashboard");
      import("./pages/Assessments");
      import("./pages/AiInsights");
    }
  });
}

const App = () => {
  useEffect(() => {
    // Read session synchronously from supabase storage to decide what to prefetch.
    // Avoid importing supabase client at module top to keep this tree-shake friendly.
    import("@/integrations/supabase/client").then(({ supabase }) => {
      supabase.auth.getSession().then(({ data }) => prefetchRoutes(!!data.session));
    });
  }, []);
  return (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              <Route path="/auth" element={<Auth />} />
              <Route path="/welcome" element={<Welcome />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/dpa" element={<DataProcessingAgreement />} />
              <Route path="/acceptable-use" element={<AcceptableUse />} />
              <Route path="/docs/srs" element={<SrsDocument />} />
              <Route path="/srs-document" element={<SrsDocument />} />
              <Route path="/services" element={<ModuleGuard module="services"><Services /></ModuleGuard>} />
              <Route path="/services/:id" element={<ModuleGuard module="services"><ServiceDetail /></ModuleGuard>} />
              
              <Route path="/financial-support" element={<Navigate to="/testbed-hub#funding" replace />} />
              <Route path="/financial-support/:id" element={<ModuleGuard module="funding"><FundingDetail /></ModuleGuard>} />
              <Route path="/explore" element={<ExplorePlatform />} />
              <Route path="/knowledge" element={<KnowledgeHub />} />
              <Route path="/india" element={<KnowledgeHub scope="india" />} />
              <Route path="/about" element={<KnowledgePage />} />
              <Route path="/assessment" element={<KnowledgePage />} />
              <Route path="/knowledge/*" element={<KnowledgePage />} />
              <Route path="/india/*" element={<KnowledgePage />} />
              <Route path="/standards/*" element={<KnowledgePage />} />
              <Route path="/metrics" element={<MetricsIndex />} />
              <Route path="/metrics/:slug" element={<MetricPage />} />
              <Route path="/press" element={<PressKit />} />
              <Route path="/methodology" element={<Methodology />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/marketplace" element={<ModuleGuard module="marketplace"><Marketplace /></ModuleGuard>} />
              <Route path="/marketplace/vendors" element={<ModuleGuard module="marketplace"><MarketplaceVendors /></ModuleGuard>} />
              <Route path="/marketplace/categories" element={<ModuleGuard module="marketplace"><MarketplaceCategories /></ModuleGuard>} />
              <Route path="/marketplace/vendor" element={<ModuleGuard module="marketplace"><VendorProfile /></ModuleGuard>} />
              <Route path="/expert-advisory" element={<ModuleGuard module="experts"><ExpertAdvisory /></ModuleGuard>} />
              <Route path="/testbed-hub" element={<TestbedHubHome />} />
              <Route path="/experts" element={<Navigate to="/expert-advisory" replace />} />
              <Route path="/testbed" element={<Navigate to="/testbed-hub" replace />} />
              <Route path="/testbed-hub/proposals" element={<TestbedProposalsList />} />
              <Route path="/testbed-hub/proposals/new" element={<TestbedProposalWizard />} />
              <Route path="/testbed-hub/proposals/:id" element={<TestbedProposalDetail />} />
              <Route path="/testbed-hub/proposals/:id/edit" element={<TestbedProposalWizard />} />
              <Route path="/testbed-hub/partnerships" element={<TestbedPartnerships />} />
              <Route path="/testbed-hub/evidence" element={<TestbedEvidence />} />
              <Route path="/testbed-hub/agents" element={<TestbedAgents />} />
              <Route path="/assessments/new" element={<ModuleGuard module="assessment"><CreateAssessment /></ModuleGuard>} />
              <Route element={<AppLayout />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/age-friendly-index" element={<AgeFriendlyIndex />} />
                <Route path="/assessments" element={<Assessments />} />
                <Route path="/assessments/:id" element={<ModuleGuard module="assessment"><AssessmentAnswer /></ModuleGuard>} />
                {/* Reports & summaries are always readable, even when assessment access is off/expired */}
                <Route path="/assessments/:id/report" element={<AssessmentReport />} />
                <Route path="/assessments/:id/summary" element={<AssessmentSummary />} />
                <Route path="/assessments/:id/audit" element={<AuditTrail />} />
                <Route path="/framework" element={<Framework />} />
                <Route path="/framework/overview" element={<ModuleGuard module="assessment"><FrameworkOverview /></ModuleGuard>} />
                <Route path="/approvals" element={<ApprovalQueue />} />
                <Route path="/approvals/:id" element={<ApproveAssessment />} />
                {/* Legacy redirects */}
                <Route path="/reviews" element={<ApprovalQueue />} />
                <Route path="/reviews/:id" element={<ApproveAssessment />} />
                <Route path="/entities" element={<Entities />} />
                <Route path="/users" element={<Users />} />
                <Route path="/invitations" element={<Invitations />} />
                <Route path="/organisations" element={<Organisations />} />
                <Route path="/ai-insights" element={<AiInsights />} />
                <Route path="/expert-profile" element={<ExpertProfile />} />
                <Route path="/expert-sessions" element={<ExpertSessions />} />
                <Route path="/my-bookings" element={<MyBookings />} />
                <Route path="/admin/service-requests" element={<AdminServiceRequests />} />
                <Route path="/admin/marketplace-leads" element={<AdminMarketplaceLeads />} />
                <Route path="/admin/vendors" element={<AdminVendors />} />
                <Route path="/vendor/dashboard" element={<VendorDashboard />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/intelligence" element={<IntelligenceHome />} />
                <Route path="/intelligence/knowledge" element={<KnowledgeRepository />} />
                {/* Research Library is now a tab within the unified Knowledge Library */}
                <Route path="/intelligence/research" element={<Navigate to="/intelligence/knowledge?tab=research" replace />} />
                <Route path="/intelligence/ingestion" element={<DocumentIngestion />} />
                <Route path="/intelligence/search" element={<SemanticSearch />} />
                <Route path="/intelligence/graph" element={<KnowledgeGraph />} />
                <Route path="/intelligence/graph-explorer" element={<KnowledgeGraphExplorer />} />
                <Route path="/intelligence/relationships" element={<RelationshipExplorer />} />
                <Route path="/intelligence/assessments" element={<AssessmentIntelligence />} />
                <Route path="/intelligence/marketplace" element={<MarketplaceIntelligence />} />
                <Route path="/intelligence/experts" element={<ExpertIntelligence />} />
                <Route path="/intelligence/datasets" element={<DatasetBuilder />} />
                <Route path="/intelligence/agents" element={<AgentManagement />} />
                <Route path="/intelligence/ontology" element={<OntologyManager />} />
                <Route path="/intelligence/slm-readiness" element={<SlmReadinessCenter />} />
                <Route path="/intelligence/model-lab" element={<ModelEvaluationLab />} />

                {/* Longevity Intelligence Platform (LIP) */}
                <Route path="/lip" element={<LipPlatformHome />} />
                <Route path="/lip/ontology" element={<LipOntologyHome />} />
                <Route path="/lip/ontology/governance" element={<LipOntologyGovernance />} />
                <Route path="/lip/corpus" element={<LipCorpusHome />} />
                <Route path="/lip/corpus/quality" element={<LipCorpusQualityMonitor />} />
                <Route path="/lip/ai" element={<LipAiLayerHome />} />
                <Route path="/lip/ai/registry" element={<LipModelRegistry />} />
                <Route path="/lip/ai/fine-tuning" element={<LipFineTuningReadiness />} />
                <Route path="/lip/applications" element={<LipApplicationsHome />} />
                <Route path="/lip/api-catalog" element={<LipApiCatalog />} />
                <Route path="/lip/roadmap" element={<LipRoadmap />} />
                <Route path="/lip/knowledge" element={<LipKnowledgeHome />} />
                <Route path="/lip/knowledge/sources" element={<LipKnowledgeSources />} />
                <Route path="/lip/knowledge/pipeline" element={<LipKnowledgePipeline />} />
                <Route path="/lip/knowledge/validation" element={<LipKnowledgeValidation />} />
                <Route path="/lip/knowledge/coverage" element={<LipKnowledgeCoverage />} />
                <Route path="/lip/knowledge/evidence" element={<LipKnowledgeEvidence />} />
                <Route path="/lip/knowledge/governance" element={<LipKnowledgeGovernance />} />
                <Route path="/lip/knowledge/gaps" element={<LipKnowledgeGaps />} />
                <Route path="/lip/knowledge/slm-readiness" element={<LipKnowledgeSlm />} />
                <Route path="/lip/knowledge/metrics/:id" element={<LipKnowledgeMetric />} />
                <Route path="/lip/knowledge/extractions" element={<LipKnowledgeExtractions />} />
                <Route path="/lip/knowledge/relationships" element={<LipKnowledgeRelationships />} />
                <Route path="/lip/knowledge/health" element={<LipKnowledgeHealth />} />
                <Route path="/lip/knowledge/pipeline/:id" element={<LipKnowledgePipelineDetail />} />
                <Route path="/lip/knowledge/retrieval" element={<LipKnowledgeRetrieval />} />
                <Route path="/lip/knowledge/public-pages" element={<LipPublicPages />} />
                <Route path="/lip/knowledge/search" element={<LipSearchMonitoring />} />
                <Route path="/lip/datasets" element={<LipDatasetFactory />} />
                <Route path="/lip/datasets/validation" element={<LipDatasetValidation />} />
                <Route path="/lip/datasets/orchestrator" element={<LipDatasetOrchestrator />} />
                <Route path="/lip/models" element={<LipModelRegistryLab />} />
                <Route path="/lip/models/comparison" element={<LipModelComparisonLab />} />
                <Route path="/lip/models/benchmarks" element={<LipBenchmarkCenter />} />
                <Route path="/lip/models/training" element={<LipTrainingLab />} />
                <Route path="/lip/models/training/:id" element={<LipTrainingRunDetail />} />


              </Route>
              <Route path="/vendor/onboarding" element={<VendorOnboarding />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
